import React, { useState } from 'react'
import { useQuery, useMutation, gql } from '@apollo/client'
import { withTranslation } from 'react-i18next'
import Header from '../components/Headers/Header'
import CustomLoader from '../components/Loader/CustomLoader'
import DataTable from '../components/DataTable'
import orderBy from 'lodash/orderBy'
import { getShopTypes, toggleShopType, deleteShopType } from '../apollo'
import SearchBar from '../components/TableHeader/SearchBar'
import useGlobalStyles from '../utils/globalStyles'
import { customStyles } from '../utils/tableCustomStyles'
import { RowActionMenu } from '../components/UI/RowActionMenu'
import EditOutlined from '@mui/icons-material/EditOutlined'
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded'
import { Box, Container, Modal, Switch, Button } from '@mui/material'
import AddRounded from '@mui/icons-material/AddRounded'
import { raisedButtonSx } from '../components/UI/Depth'
import TableHeader from '../components/TableHeader'
import ShopTypeComponent from '../components/ShopType/ShopType'
import { modalOverlay, modalCard } from '../utils/modalStyles'

const GET_SHOP_TYPES = gql`
  ${getShopTypes}
`
const TOGGLE_SHOP_TYPE = gql`
  ${toggleShopType}
`
const DELETE_SHOP_TYPE = gql`
  ${deleteShopType}
`

// A real component rather than a function called from the cell renderer: the
// menu keeps state in hooks, and hooks called out of a plain helper belong to
// whichever component happens to be rendering, which leaks one row's open menu
// into another row.
const RowActions = ({ row, onEdit, onDelete, t }) => {
  return (
    <div>
      <RowActionMenu
        label={t('Action')}
        actions={[
          {
            label: t('Edit'),
            icon: EditOutlined,
            tone: 'primary',
            onClick: () => {
              onEdit(row)
            }
          },
          {
            label: t('Delete'),
            icon: DeleteOutlineRounded,
            tone: 'danger',
            divider: true,
            confirm: {
              title: `${t('Delete')}?`,
              message: t('DeleteConfirm'),
              cancelLabel: t('Cancel')
            },
            onClick: () => {
              onDelete(row)
            }
          }
        ]}
      />
    </div>
  )
}

const ShopTypes = props => {
  const { t } = props
  const [editModal, setEditModal] = useState(false)
  const [shopType, setShopType] = useState(null)
  const [addModal, setAddModal] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const onChangeSearch = e => setSearchQuery(e.target.value)

  const [mutateToggle] = useMutation(TOGGLE_SHOP_TYPE)
  const [mutateDelete] = useMutation(DELETE_SHOP_TYPE, {
    refetchQueries: [{ query: GET_SHOP_TYPES }]
  })
  const { data, error: errorQuery, loading: loadingQuery, refetch } = useQuery(
    GET_SHOP_TYPES
  )

  // Explicit open and close. Toggling meant clicking Edit on a second row while
  // the dialog was open closed it and left the wrong row selected.
  const openModal = row => {
    setShopType(row)
    setEditModal(true)
  }

  const closeModal = () => {
    setEditModal(false)
    setShopType(null)
  }

  const customSort = (rows, field, direction) => {
    const handleField = row => {
      if (row[field] && isNaN(row[field])) {
        return row[field].toLowerCase()
      }

      return row[field]
    }

    return orderBy(rows, handleField, direction)
  }

  const columns = [
    {
      name: t('Icon'),
      cell: row =>
        row.image ? (
          <img
            src={row.image}
            alt={row.title}
            style={{ width: 48, height: 48, objectFit: 'contain' }}
          />
        ) : (
          <span style={{ fontSize: 22 }}>{row.emoji || '-'}</span>
        )
    },
    {
      name: t('Name'),
      sortable: true,
      selector: 'title'
    },
    {
      name: t('Description'),
      sortable: true,
      selector: 'description'
    },
    {
      name: t('HomeLayout'),
      sortable: true,
      selector: 'displayVariant',
      cell: row => (
        <span>
          {row.displayVariant === 'grid' ? t('VariantGrid') : t('VariantRow')}
        </span>
      )
    },
    {
      name: t('Status'),
      cell: row => (
        <Switch
          size="small"
          checked={!!row.isActive}
          onChange={() => {
            mutateToggle({ variables: { id: row._id } })
          }}
        />
      )
    },
    {
      name: t('Action'),
      cell: row => (
        <RowActions
          row={row}
          t={t}
          onEdit={openModal}
          onDelete={deleted => mutateDelete({ variables: { id: deleted._id } })}
        />
      )
    }
  ]

  const regex =
    searchQuery.length > 2 ? new RegExp(searchQuery.toLowerCase(), 'g') : null
  const filtered =
    searchQuery.length < 3
      ? data && data.shopTypes
      : data &&
        data.shopTypes.filter(shopType => {
          return shopType.title.toLowerCase().search(regex) > -1
        })

  const globalClasses = useGlobalStyles()
  return (
    <>
      <Header
        action={
          <Button
            startIcon={<AddRounded />}
            onClick={() => setAddModal(true)}
            sx={raisedButtonSx}>
            {`${t('Add')} ${t('Shop Type')}`}
          </Button>
        }
      />
      {/* Page content */}
      <Container className={globalClasses.flex} fluid>
        {errorQuery ? (
          <span>
            `${t('Error')}! ${errorQuery.message}`
          </span>
        ) : null}
        {loadingQuery ? (
          <CustomLoader />
        ) : (
          <DataTable
            subHeader={true}
            subHeaderComponent={
              <SearchBar
                value={searchQuery}
                onChange={onChangeSearch}
                onClick={() => refetch()}
              />
            }
            title={<TableHeader title={t('ShopTypes')} />}
            columns={columns}
            data={filtered}
            pagination
            progressPending={loadingQuery}
            progressComponent={<CustomLoader />}
            sortFunction={customSort}
            defaultSortField="title"
            customStyles={customStyles}
          />
        )}
        <Modal
          open={editModal}
          onClose={closeModal}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            // The form is taller than a laptop viewport, so without its own
            // scroll the Save button sits below the fold, unreachable.
            overflowY: 'auto',
            padding: 16
          }}>
          <Box sx={{ maxHeight: '90vh', overflowY: 'auto', outline: 'none' }}>
            {/* Keyed by row so reopening on a different shop type mounts a
                fresh form rather than reusing the previous row's fields. */}
            <ShopTypeComponent key={shopType?._id} shopType={shopType} />
          </Box>
        </Modal>
        <Modal
          open={addModal}
          onClose={() => setAddModal(false)}
          sx={modalOverlay}>
          <Box sx={modalCard('md')}>
            <ShopTypeComponent onClose={() => setAddModal(false)} />
          </Box>
        </Modal>
      </Container>
    </>
  )
}

export default withTranslation()(ShopTypes)
